import React, { useState } from 'react';
import { Button, ButtonType, ButtonPropsType } from 'components/Controls/Button';
import { ModalProps as AntdModalProps } from 'antd/lib/modal';
import { Modal } from '../index';
import cn from './styles.module.scss';

export interface ConfirmModalProps extends AntdModalProps {
  onConfirm: () => void,
  onCancel?: () => void,
  children?: React.ReactNode,
  content?: React.ReactNode,
  title?: React.ReactNode,
  okText?: React.ReactNode,
  cancelText?: React.ReactNode,
  confirmButtonProps?: Pick<ButtonPropsType, 'disabled'>
}

export const ConfirmModal = ({
  title,
  onCancel,
  onConfirm,
  content,
  okText,
  cancelText,
  children,
  confirmButtonProps,
  ...props
}: ConfirmModalProps) => {
  const [visible, setVisible] = useState(false);
  const [disabled, setDisabled] = useState(false);

  const openModal = () => setVisible(true);
  const closeModal = () => setVisible(false);

  const handleConfirm = async () => {
    setDisabled(true);
    try {
      await onConfirm();
      setDisabled(false);
      closeModal();
    } catch (err) {
      setDisabled(false);
      throw err;
    }
  };

  const handleCancel = () => {
    closeModal();
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div>
      <Modal
        className={cn.confirmModal}
        open={visible}
        onClose={handleCancel}
        title={title || 'Confirm Action'}
        destroyOnClose
        {...props}
      >
        <div data-testid="confirm-modal-content">{content}</div>
        <div className={cn.actions}>
          <Button
            data-testid="confirm-modal-cancel-button"
            size="large"
            disabled={disabled}
            onClick={handleCancel}
            type={ButtonType.Secondary}
          >
            {cancelText || 'Cancel'}
          </Button>
          <Button
            data-testid="confirm-modal-confirm-button"
            size="large"
            onClick={handleConfirm}
            type={ButtonType.Primary}
            disabled={disabled || confirmButtonProps?.disabled}
          >
            {okText || 'Confirm'}
          </Button>
        </div>
      </Modal>
      <div onClick={openModal} data-testid="confirm-modal-trigger">
        {children}
      </div>
    </div>
  );
};
